import React, { useState, useMemo, useCallback } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Button, buttonVariants } from "./ui/button";
import { Link } from 'react-router-dom';
import { Trash2, X, Calendar, FolderTree, Save, ArrowUpDown, ChevronDown, Folder, Pin, PinOff } from 'lucide-react';
import { cn } from "@/lib/utils";
import { DatePicker } from "./ui/date-picker";
import { addDays, format } from "date-fns";
import { compareOsloDates, osloDateToPickerDate, toOsloDateString } from '@/lib/osloDates';
import { db } from '../config/firebase';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import CartCounter from './CartCounter';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

// Rental factor table based on provided image, extended to 200 days
const rentalFactors = [
  { from: 1, to: 1, factor: 1 },
  { from: 2, to: 2, factor: 2 },
  { from: 3, to: 7, factor: 3 },
  { from: 8, to: 8, factor: 4 },
  { from: 9, to: 9, factor: 5 },
  { from: 10, to: 14, factor: 6 },
  { from: 15, to: 15, factor: 7 },
  { from: 16, to: 16, factor: 8 },
  { from: 17, to: 21, factor: 9 },
  { from: 22, to: 22, factor: 10 },
  { from: 23, to: 23, factor: 11 },
  { from: 24, to: 28, factor: 12 },
  { from: 29, to: 29, factor: 13 },
  { from: 30, to: 30, factor: 14 },
  { from: 31, to: 35, factor: 15 },
  { from: 36, to: 36, factor: 16 },
  { from: 37, to: 37, factor: 17 },
  { from: 38, to: 42, factor: 18 },
  { from: 43, to: 43, factor: 19 },
  { from: 44, to: 44, factor: 20 },
  { from: 45, to: 49, factor: 21 },
  { from: 50, to: 50, factor: 22 },
  { from: 51, to: 51, factor: 23 },
  { from: 52, to: 56, factor: 24 },
  { from: 57, to: 57, factor: 25 },
  { from: 58, to: 58, factor: 26 },
  { from: 59, to: 63, factor: 27 },
  { from: 64, to: 64, factor: 28 },
  { from: 65, to: 65, factor: 29 },
  { from: 66, to: 70, factor: 30 },
  { from: 71, to: 71, factor: 31 },
  { from: 72, to: 72, factor: 32 },
  { from: 73, to: 77, factor: 33 },
  { from: 78, to: 78, factor: 34 },
  { from: 79, to: 79, factor: 35 },
  { from: 80, to: 84, factor: 36 },
  { from: 85, to: 85, factor: 37 },
  { from: 86, to: 86, factor: 38 },
  { from: 87, to: 91, factor: 39 },
  { from: 92, to: 92, factor: 40 },
  { from: 93, to: 93, factor: 41 },
  { from: 94, to: 98, factor: 42 },
  { from: 99, to: 99, factor: 43 },
  { from: 100, to: 100, factor: 44 },
  { from: 101, to: 105, factor: 45 },
  { from: 106, to: 106, factor: 46 },
  { from: 107, to: 107, factor: 47 },
  { from: 108, to: 112, factor: 48 },
  { from: 113, to: 113, factor: 49 },
  { from: 114, to: 114, factor: 50 },
  { from: 115, to: 119, factor: 51 },
  { from: 120, to: 120, factor: 52 },
  { from: 121, to: 121, factor: 53 },
  { from: 122, to: 126, factor: 54 },
  { from: 127, to: 127, factor: 55 },
  { from: 128, to: 128, factor: 56 },
  { from: 129, to: 133, factor: 57 },
  { from: 134, to: 134, factor: 58 },
  { from: 135, to: 135, factor: 59 },
  { from: 136, to: 140, factor: 60 },
  { from: 141, to: 141, factor: 61 },
  { from: 142, to: 142, factor: 62 },
  { from: 143, to: 147, factor: 63 },
  { from: 148, to: 148, factor: 64 },
  { from: 149, to: 149, factor: 65 },
  { from: 150, to: 150, factor: 66 },
  { from: 151, to: 155, factor: 67 },
  { from: 156, to: 156, factor: 68 },
  { from: 157, to: 157, factor: 69 },
  { from: 158, to: 162, factor: 70 },
  { from: 163, to: 163, factor: 71 },
  { from: 164, to: 164, factor: 72 },
  { from: 165, to: 169, factor: 73 },
  { from: 170, to: 170, factor: 74 },
  { from: 171, to: 171, factor: 75 },
  { from: 172, to: 176, factor: 76 },
  { from: 177, to: 177, factor: 77 },
  { from: 178, to: 178, factor: 78 },
  { from: 179, to: 183, factor: 79 },
  { from: 184, to: 184, factor: 80 },
  { from: 185, to: 185, factor: 81 },
  { from: 186, to: 190, factor: 82 },
  { from: 191, to: 191, factor: 83 },
  { from: 192, to: 192, factor: 84 },
  { from: 193, to: 197, factor: 85 },
  { from: 198, to: 198, factor: 86 },
  { from: 199, to: 199, factor: 87 },
  { from: 200, to: 200, factor: 88 },
];

function getRentalFactor(days) {
  for (const entry of rentalFactors) {
    if (days >= entry.from && days <= entry.to) {
      return entry.factor;
    }
  }
  // If days exceed the last range, use the last factor
  return rentalFactors[rentalFactors.length - 1].factor;
}

const CartSidebar = ({ isOpen, onClose, onPinChange }) => {
  const { 
    cartItems, 
    getCartTotalPriceWithFactor,
    calculatePriceWithFactor,
    getTaxAmount,
    getProtectionAmount,
    getGrandTotal,
    daysOfRent,
    rentalFactor,
    removeItemCompletely, 
    clearCart,
    checkoutDate,
    setCheckoutDate,
    checkinDate,
    setCheckinDate,
    shootStartDate,
    setShootStartDate,
    shootEndDate,
    setShootEndDate,
  } = useCart();
  const { currentUser, currentUserProfile } = useAuth();
  const { rentmanData, isCartPinned, setIsCartPinned } = useApp();
  const isApproved = currentUserProfile?.approvalStatus === 'approved';
  const [sortBy, setSortBy] = useState('none');
  const [groupBy, setGroupBy] = useState('none');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [orderName, setOrderName] = useState('');

  // Handle pin state changes
  const handlePinChange = (pinned) => {
    setIsCartPinned(pinned);
    onPinChange?.(pinned);
  };

  const getFolderOrderIndex = useCallback((folderName = "") => {
    const match = String(folderName).match(/^(\d+)\.\s*/);
    return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
  }, []);

  const getDefaultGroupName = useCallback((item) => {
    const raw = item?.defaultgroup ?? item?.defaultGroup ?? item?.default_group ?? null;
    if (typeof raw === "string" && raw.trim().length > 0) return raw.trim();
    if (raw && typeof raw === "object") {
      if (typeof raw.name === "string" && raw.name.trim().length > 0) return raw.name.trim();
      if (typeof raw.displayname === "string" && raw.displayname.trim().length > 0) return raw.displayname.trim();
      if (typeof raw.displayName === "string" && raw.displayName.trim().length > 0) return raw.displayName.trim();
    }
    return "Uncategorized";
  }, []);

  const { rootFolderByItemId, immediateFolderByItemId } = useMemo(() => {
    const rootMap = new Map();
    const immediateMap = new Map();

    const walk = (folders = [], rootFolderName = null) => {
      for (const folder of folders) {
        const nextRootName = rootFolderName || folder.name || "Uncategorized";
        for (const item of folder.items || []) {
          const itemId = String(item.id);
          rootMap.set(itemId, nextRootName);
          immediateMap.set(itemId, folder.name || "Uncategorized");
        }
        walk(folder.children || [], nextRootName);
      }
    };

    walk(rentmanData?.tree || []);
    return { rootFolderByItemId: rootMap, immediateFolderByItemId: immediateMap };
  }, [rentmanData]);

  // Sort and group items
  const groupedItems = useMemo(() => {
    const compareItems = (a, b) => {
      const aUnitPrice = Number(a.price) || 0;
      const bUnitPrice = Number(b.price) || 0;
      
      switch (sortBy) {
        case 'price-asc':
          return aUnitPrice - bUnitPrice;
        case 'price-desc':
          return bUnitPrice - aUnitPrice;
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'none':
        default:
          return String(a.name || '').localeCompare(String(b.name || ''));
      }
    };

    if (groupBy === 'none') {
      const sortedItems = [...cartItems].sort(compareItems);
      return { 'All Items': sortedItems };
    }

    const resolveGroupName = (item) => {
      const key = String(item.id);
      if (groupBy === 'category') {
        return getDefaultGroupName(item);
      }
      return rootFolderByItemId.get(key) || 'Uncategorized';
    };

    const groups = new Map();
    cartItems.forEach((item) => {
      const folderName = resolveGroupName(item);
      if (!groups.has(folderName)) {
        groups.set(folderName, []);
      }
      groups.get(folderName).push(item);
    });

    const sortedGroupNames = Array.from(groups.keys()).sort((a, b) => {
      const orderDiff = getFolderOrderIndex(a) - getFolderOrderIndex(b);
      if (orderDiff !== 0) return orderDiff;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });

    const orderedGroups = {};
    sortedGroupNames.forEach((groupName) => {
      orderedGroups[groupName] = [...(groups.get(groupName) || [])].sort(compareItems);
    });

    return orderedGroups;
  }, [
    cartItems,
    sortBy,
    groupBy,
    calculatePriceWithFactor,
    getDefaultGroupName,
    immediateFolderByItemId,
    rootFolderByItemId,
    getFolderOrderIndex,
  ]);

  // Get sort label
  const getSortLabel = (value) => {
    switch (value) {
      case 'price-asc': return 'Price: Low to High';
      case 'price-desc': return 'Price: High to Low';
      case 'name-asc': return 'Name: A to Z';
      case 'name-desc': return 'Name: Z to A';
      case 'none': return 'No Sorting';
      default: return 'Sort by';
    }
  };

  // Get group label
  const getGroupLabel = (value) => {
    switch (value) {
      case 'folder': return 'Equipment Group';
      case 'category': return 'Equipment Category';
      case 'none': return 'No Grouping';
      default: return 'Group by';
    }
  };

  const handleSaveOrder = async () => {
    if (!orderName.trim()) {
      toast.error('Please enter an order name');
      return;
    }

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        toast.error('User document not found');
        return;
      }

      const userData = userDoc.data();
      const newOrder = {
        id: crypto.randomUUID(),
        name: orderName.trim(),
        items: cartItems.map(item => ({
          ...item,
          quantity: parseInt(item.quantity) || 1
        })),
        createdAt: new Date().toISOString()
      };

      // Add to savedOrders array in user document
      const updatedSavedOrders = [...(userData.savedOrders || []), newOrder];

      await updateDoc(userDocRef, {
        savedOrders: updatedSavedOrders
      });

      toast.success('Order saved successfully');
      setSaveDialogOpen(false);
      setOrderName('');
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Failed to save order');
    }
  };

  const handleCheckoutChange = useCallback((date) => {
    setCheckoutDate(toOsloDateString(date));
    setShootStartDate(null);
    setShootEndDate(null);
    setCheckinDate(null);
  }, [setCheckoutDate, setShootStartDate, setShootEndDate, setCheckinDate]);

  const handleShootStartChange = useCallback((date) => {
    const next = toOsloDateString(date);
    if (next && checkoutDate && compareOsloDates(next, checkoutDate) < 0) {
      toast.error("Invalid Shoot Start", { description: "Shoot start cannot be before check out date." });
      return;
    }
    setShootStartDate(next);
    setShootEndDate(null);
    setCheckinDate(null);
  }, [checkoutDate, setShootStartDate, setShootEndDate, setCheckinDate]);

  const handleShootEndChange = useCallback((date) => {
    const next = toOsloDateString(date);
    if (next && shootStartDate && compareOsloDates(next, shootStartDate) < 0) {
      toast.error("Invalid Shoot End", { description: "Shoot end cannot be before shoot start." });
      return;
    }
    setShootEndDate(next);
    setCheckinDate(null);
  }, [shootStartDate, setShootEndDate, setCheckinDate]);

  const handleCheckinChange = useCallback((date) => {
    const next = toOsloDateString(date);
    if (next && shootEndDate && compareOsloDates(next, shootEndDate) < 0) {
      toast.error("Invalid Check in", { description: "Check in cannot be before shoot end." });
      return;
    }
    setCheckinDate(next);
  }, [shootEndDate, setCheckinDate]);

  const dateSummary = useMemo(() => {
    const formatOsloDate = (osloDate) => {
      const pickerDate = osloDateToPickerDate(osloDate);
      return pickerDate ? format(pickerDate, "d MMM") : null;
    };
    const checkoutLabel = formatOsloDate(checkoutDate);
    const checkinLabel = formatOsloDate(checkinDate);
    if (checkoutLabel && checkinLabel) return `${checkoutLabel} – ${checkinLabel}`;
    if (checkoutLabel) return `From ${checkoutLabel}`;
    return "Pick dates";
  }, [checkoutDate, checkinDate]);

  // If user is not approved, don't show the cart
  if (!isApproved) {
    return null;
  }

  return (
    <>
      {/* Backdrop - only show when not pinned */}
      {isOpen && !isCartPinned && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed right-0 top-[var(--header-height)] h-[calc(100vh-var(--header-height))] bg-card border-l border-zinc-700 shadow-lg z-50 transform transition-transform duration-300 ease-in-out",
        "w-full md:w-[600px]",
        isOpen ? "translate-x-0" : "translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Fixed Header */}
          <div className="flex-none">
            <div className="p-3 border-b border-zinc-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-card-foreground">Request</h2>
                <div className="flex items-center gap-2">
                  {/* Pin button only on screens wider than 1300px */}
                  <div className="hidden [@media(min-width:1300px)]:block">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handlePinChange(!isCartPinned)}
                      className="h-8 w-8"
                      title={isCartPinned ? "Unpin sidebar" : "Pin sidebar"}
                    >
                      {isCartPinned ? (
                        <PinOff className="h-4 w-4" />
                      ) : (
                        <Pin className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="[@media(min-width:1300px)]:hidden">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-50 cursor-not-allowed"
                            disabled
                          >
                            <Pin className="h-4 w-4" />
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Pinning is only available on screens wider than 1300px</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            {/* Controls Section */}
            <div className="p-3 border-b border-zinc-700">
              <div className="flex flex-row items-center gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">Sort</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="w-[90px] px-2 text-xs justify-between">
                        {(() => {
                          switch (sortBy) {
                            case 'price-asc': return 'Low';
                            case 'price-desc': return 'High';
                            case 'name-asc': return 'A-Z';
                            case 'name-desc': return 'Z-A';
                            default: return 'None';
                          }
                        })()}
                        <ChevronDown className="h-3 w-3 opacity-50 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => setSortBy('none')}>
                        <span className="md:hidden">None</span>
                        <span className="hidden md:inline">No Sorting</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSortBy('price-asc')}>
                        <span className="md:hidden">Low</span>
                        <span className="hidden md:inline">Price: Low to High</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSortBy('price-desc')}>
                        <span className="md:hidden">High</span>
                        <span className="hidden md:inline">Price: High to Low</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSortBy('name-asc')}>
                        <span className="md:hidden">A-Z</span>
                        <span className="hidden md:inline">Name: A to Z</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSortBy('name-desc')}>
                        <span className="md:hidden">Z-A</span>
                        <span className="hidden md:inline">Name: Z to A</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">Group</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="w-[90px] px-2 text-xs justify-between">
                        {(() => {
                          switch (groupBy) {
                            case 'folder': return 'Group';
                            case 'category': return 'Category';
                            default: return 'None';
                          }
                        })()}
                        <ChevronDown className="h-3 w-3 opacity-50 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => setGroupBy('none')}>
                        <span className="md:hidden">None</span>
                        <span className="hidden md:inline">No Grouping</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setGroupBy('folder')}>
                        <span className="md:hidden">Group</span>
                        <span className="hidden md:inline">Equipment Group</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setGroupBy('category')}>
                        <span className="md:hidden">Category</span>
                        <span className="hidden md:inline">Equipment Category</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Table Section */}
          <div className="flex-1 min-h-0">
            <ScrollArea className="h-full">
              <div className="p-3">
                <div className="space-y-2">
                  {/* Table Header - Fixed */}
                  <div className="sticky top-0 z-10 grid grid-cols-[2fr,1fr,0.5fr,0.5fr,1fr,0.5fr] gap-2 px-3 py-1.5 text-xs font-medium text-foreground bg-[#20292e] border-b border-border rounded-t-lg">
                    <div>Product</div>
                    <div>Rate</div>
                    <div>Qty</div>
                    <div>Factor</div>
                    <div>Total</div>
                    <div></div>
                  </div>
                  {Object.entries(groupedItems).map(([folderName, items]) => (
                    <div key={folderName} className="rounded-lg overflow-hidden">
                      <div className="bg-[#171E22] px-3 py-2 flex items-center">
                        <h4 className="text-sm font-semibold text-primary">{folderName}</h4>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {items.length} {items.length === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                      <div>
                        {/* Items */}
                        <div>
                          {items.map((item, index) => (
                            <div 
                              key={item.id} 
                              className={cn(
                                "grid grid-cols-[2fr,1fr,0.5fr,0.5fr,1fr,0.5fr] gap-2 px-3 py-2 items-center",
                                index % 2 === 0 ? "bg-background" : "bg-muted/10",
                                "hover:bg-muted/20"
                              )}
                            >
                              <div>
                                <div className="font-medium text-sm text-foreground">{item.name}</div>
                                {item.itemRemark && (
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    {item.itemRemark}
                                  </div>
                                )}
                              </div>
                              <div className="text-sm text-foreground">{item.price} NOK</div>
                              <div className="text-sm text-foreground"><CartCounter item={item} /></div>
                              <div className="text-sm text-foreground">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      {rentalFactor}x
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{daysOfRent} days</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <div className="text-sm font-medium text-foreground">{calculatePriceWithFactor(item.price, item.quantity)} NOK</div>
                              <div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeItemCompletely(item.id)}
                                  className="h-7 w-7"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollArea>
          </div>

          {/* Fixed Bottom Section */}
          <div className="flex-none border-t border-zinc-700">
            <div className="p-3 space-y-2">
              <button
                type="button"
                onClick={() => setDetailsOpen((open) => !open)}
                className="flex w-full items-center justify-between text-sm font-medium text-foreground"
              >
                <span>Dates & price</span>
                <span className="flex items-center gap-2">
                  {!detailsOpen && (
                    <span className="text-xs font-normal text-muted-foreground">{dateSummary}</span>
                  )}
                  <ChevronDown className={cn("h-4 w-4 opacity-70 transition-transform", detailsOpen && "rotate-180")} />
                </span>
              </button>
              {detailsOpen && (
                <div className="space-y-4 pt-1">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-foreground mb-1">
                        Check out
                      </h3>
                      <DatePicker
                        value={osloDateToPickerDate(checkoutDate)}
                        onSelect={handleCheckoutChange}
                        disabled={{ 
                          before: new Date(),
                          after: addDays(new Date(), 365)
                        }}
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground mb-1">
                        Shoot Start
                      </h3>
                      <DatePicker
                        value={osloDateToPickerDate(shootStartDate)}
                        onSelect={handleShootStartChange}
                        disabled={{
                          before: osloDateToPickerDate(checkoutDate) || new Date(),
                          after: addDays(new Date(), 365),
                        }}
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground mb-1">
                        Shoot End
                      </h3>
                      <DatePicker
                        value={osloDateToPickerDate(shootEndDate)}
                        onSelect={handleShootEndChange}
                        disabled={{
                          before: osloDateToPickerDate(shootStartDate) || osloDateToPickerDate(checkoutDate) || new Date(),
                          after: addDays(new Date(), 365),
                        }}
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground mb-1">
                        Check in
                      </h3>
                      <DatePicker
                        value={osloDateToPickerDate(checkinDate)}
                        onSelect={handleCheckinChange}
                        disabled={{
                          before: osloDateToPickerDate(shootEndDate) || osloDateToPickerDate(checkoutDate) || new Date(),
                          after: addDays(new Date(), 365),
                        }}
                      />
                    </div>
                  </div>
                  {cartItems.length > 0 && (
                    <div className="space-y-2">
                      <Separator />
                      <div className="flex items-center justify-between text-sm">
                        <span>Sub Total</span>
                        <span>{getCartTotalPriceWithFactor()} NOK</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Tax (25%)</span>
                        <span>{getTaxAmount()} NOK</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Insurance (5%)</span>
                        <span>{getProtectionAmount()} NOK</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {cartItems.length > 0 && (
                <>
                  <Separator className="my-1" />
                  <div className="flex items-center justify-between font-medium">
                    <span>Total</span>
                    <span>{getGrandTotal()} NOK</span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="flex-1"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Clear
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Clear Entire Request?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. All items will be removed from your request.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={clearCart} className={cn(buttonVariants({ variant: "destructive" }))}>
                            Yes, Clear Request
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setSaveDialogOpen(true)}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                    <Link to="/checkout" className="flex-1">
                      <Button className="w-full">
                        Save & Request
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Save Order Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Current Request</DialogTitle>
            <DialogDescription>
              Give your saved request a name for future reference.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="orderName">Request Name</Label>
            <Input
              id="orderName"
              value={orderName}
              onChange={(e) => setOrderName(e.target.value)}
              placeholder="Enter request name"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveOrder}>
              Save Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CartSidebar; 